import { v4 as uuid } from "uuid";
import { EvalError } from "../simulation/EvalError";
import { parseAndThrowProper } from "../simulation/simFromSrc";
import { sampleDistribution } from "./montecarlo";
export const createExperimentDefinition = (experimentsSrc, experimentName) => {
    const experimentDefinitions = parseAndThrowProper(experimentsSrc, "experiments.json");
    return experimentDefinitions[experimentName];
};
export const createExperimentPlan = (experimentsSrc, experimentName) => {
    const experimentDefinitions = parseAndThrowProper(experimentsSrc, "experiments.json");
    const selectedExperiment = experimentDefinitions[experimentName];
    if (selectedExperiment.type === "optimization") {
        throw new Error("Cannot run optimization experiment locally");
    }
    return [
        createExperimentVariants(experimentName, experimentDefinitions),
        selectedExperiment,
    ];
};
export const createExperimentVariants = (experimentName, experimentDefinitions) => {
    const selectedExperiment = experimentDefinitions[experimentName];
    if (!selectedExperiment) {
        throw new Error(`No experiment with name '${experimentName}' found in experiments.json`);
    }
    switch (selectedExperiment.type) {
        case "group":
            return createGroupVariant(selectedExperiment, experimentDefinitions);
        case "multiparameter":
            return createMultiparameterVariant(selectedExperiment, experimentDefinitions);
        default:
            return createExperimentPlanFromArgs(selectedExperiment);
    }
};
const createExperimentPlanFromArgs = (selectedExperiment) => {
    switch (selectedExperiment.type) {
        case "monte-carlo":
            return createMonteCarloVariant(selectedExperiment);
        case "values":
            return createValueVariant(selectedExperiment);
        case "linspace":
            return createLinspaceVariant(selectedExperiment);
        case "arange":
            return createArangeVariant(selectedExperiment);
        case "meshgrid":
            return createMeshgridVariant(selectedExperiment);
        default:
            throw new EvalError("Not a valid experiment type, see docs.hash.ai/experiments on how to define an experiment run", "experiments.json");
    }
};
const createVariantWithMappedValue = (field, items, mapper) => items.reduce((acc, val, idx) => {
    const mappedValue = mapper(val, idx);
    acc[uuid()] = {
        fields: {
            [field]: mappedValue,
        },
    };
    return acc;
}, {});
const arange = (start, stop, step) => {
    const samples = [];
    for (let cur = start; cur <= stop; cur += step) {
        samples.push(cur);
    }
    return samples;
};
const linspace = (start, stop, numSamples) => {
    const samples = [];
    const length = (stop - start) / (numSamples - 1);
    for (let idx = start; idx <= stop; idx += length) {
        const val = start + length * idx;
        samples.push(val);
    }
    return samples;
};
const createValueVariant = (definition) => createVariantWithMappedValue(definition.field, definition.values, (val) => val);
const createLinspaceVariant = ({ field, samples, start, stop, }) => createVariantWithMappedValue(field, Array(samples).fill(0), (_, idx) => start + (idx * (stop - start)) / (samples - 1));
const createMeshgridVariant = ({ xfield, yfield, steps, x, y, }) => {
    const plan = {};
    const xspace = linspace(...x);
    const yspace = linspace(...y);
    let idx = 0;
    for (const xVal of xspace) {
        for (const yVal of yspace) {
            plan[uuid()] = {
                fields: {
                    [xfield]: xVal,
                    [yfield]: yVal,
                },
            };
            idx += 1;
        }
    }
    return plan;
};
const createMultiparameterVariant = (definition, experimentDefinitions) => {
    const parameterList = definition.runs.map((runName) => {
        const definition = experimentDefinitions[runName];
        return createExperimentPlanFromArgs(definition);
    });
    let variantList = [];
    let idx = 0;
    for (const subplan of parameterList) {
        if (idx === 0) {
            variantList = Object.values(subplan).map((entry) => entry.fields);
        }
        else {
            const newVariants = [];
            for (const entry of Object.values(subplan).map((entry) => entry.fields)) {
                for (const existingEntry of variantList) {
                    newVariants.push({
                        ...existingEntry,
                        ...entry,
                    });
                }
            }
            variantList = newVariants;
        }
        idx += 1;
    }
    const finalPlan = {};
    let index = 0;
    for (const variant of variantList) {
        finalPlan[uuid()] = {
            fields: variant,
        };
        index += 1;
    }
    return finalPlan;
};
const createArangeVariant = ({ start, stop, increment, field, }) => createVariantWithMappedValue(field, arange(start, stop, increment), (val) => val);
const createGroupVariant = (definition, experimentDefinitions) => {
    const plan = definition.runs.reduce((acc, name) => ({
        ...acc,
        ...createExperimentVariants(name, experimentDefinitions),
    }), {});
    return plan;
};
export const createMonteCarloVariant = (definition) => createVariantWithMappedValue(definition.field, Array(definition.samples).fill(0), () => sampleDistribution(definition));
//# sourceMappingURL=definition.js.map